import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

/**
 * Click-to-copy for ids, emails and refs that get pasted into other pages. Icon-only
 * by default; pass `children` to render the value itself as the clickable text.
 */
export function CopyButton({
  value,
  label = "Copy",
  className,
  children,
}: {
  value: string;
  /** Accessible name, e.g. "Copy email". */
  label?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  const [done, setDone] = useState(false);
  const copy = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1500);
    } catch {
      toast({ variant: "destructive", title: "Couldn't copy", description: value });
    }
  };
  if (children) {
    return (
      <button
        type="button"
        onClick={copy}
        title={label}
        className={cn(
          "inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className,
        )}
        data-testid="copy-text"
      >
        {children}
        {done ? (
          <Check className="w-3 h-3 shrink-0 text-success-solid" />
        ) : (
          <Copy className="w-3 h-3 shrink-0 opacity-50" />
        )}
      </button>
    );
  }
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      aria-label={label}
      title={label}
      onClick={copy}
      className={cn("h-6 w-6 shrink-0 text-muted-foreground", className)}
      data-testid="copy-button"
    >
      {done ? (
        <Check className="w-3.5 h-3.5 text-success-solid" />
      ) : (
        <Copy className="w-3.5 h-3.5" />
      )}
    </Button>
  );
}
